<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import { loginAdmin } from "@/utils/api_backend";
import { useAuthStore } from "@/store/useAuthStore";
import { ROLE_HOME } from "@/config/navigation";

const email = ref("");
const password = ref("");
const recordarme = ref(true);
const router = useRouter();
const authStore = useAuthStore();
const isSubmitting = ref(false);

async function validarCredenciales() {
  if (!email.value || !password.value) {
    Swal.fire({
      icon: "warning",
      title: "Campos requeridos",
      text: "Por favor ingresa tu correo y contraseña.",
      confirmButtonColor: "#1a1a1a",
    });
    return;
  }

  isSubmitting.value = true;
  try {
    const data = await loginAdmin(email.value, password.value);

    // ── Redirigir según rol ────────────────────────────────
    const destino = ROLE_HOME[data.user.role];
    if (destino) {
      authStore.setSession(data, recordarme.value);
      router.push(destino);
    } else {
      // Rol desconocido → limpiar y avisar
      authStore.logout();
      Swal.fire({
        icon: "error",
        title: "Sin acceso",
        text: "Tu cuenta no tiene un módulo asignado. Contacta al administrador.",
        confirmButtonColor: "#d33",
      });
    }
  } catch (error) {
    authStore.logout();
    Swal.fire({
      icon: "error",
      title: "Acceso denegado",
      text: "Credenciales inválidas. Verifica tu correo y contraseña.",
      confirmButtonColor: "#d33",
    });
  } finally {
    isSubmitting.value = false;
  }
}
</script>

<template>
  <div class="login-wrapper">
    <div class="glass-login shadow-2xl" data-aos="zoom-in-down">
      <div class="text-center mb-4">
        <div class="brand-logo-container mb-3">
          <img
            src="@/assets/img/Divisan/logo_divisan.png"
            alt="Divisan Logo"
            class="brand-logo-img"
          />
        </div>
        <p class="text-white-50 small tracking-widest text-uppercase">
          Gestión de información
        </p>
      </div>

      <form @submit.prevent="validarCredenciales">
        <div class="mb-4">
          <label class="form-label text-white-50 small fw-bold text-uppercase"
            >Correo Electrónico</label
          >
          <div class="input-group-modern">
            <i class="bi bi-envelope-fill"></i>
            <input
              v-model="email"
              type="email"
              placeholder="admin@divisan.com"
              required
            />
          </div>
        </div>

        <div class="mb-4">
          <label class="form-label text-white-50 small fw-bold text-uppercase"
            >Contraseña</label
          >
          <div class="input-group-modern">
            <i class="bi bi-lock-fill"></i>
            <input v-model="password" type="password" placeholder="••••••••" required />
          </div>
        </div>

        <label class="checkbox-recordarme mb-4">
          <input v-model="recordarme" type="checkbox" />
          <span>Recordarme en este dispositivo</span>
        </label>

        <button class="btn-login" type="submit" :disabled="isSubmitting">
          <span v-if="!isSubmitting">ENTRAR AL SISTEMA</span>
          <span v-else class="spinner-border spinner-border-sm"></span>
        </button>
      </form>
    </div>
  </div>
</template>

<style scoped>
.login-wrapper {
  min-height: 100vh;
  font-family: "Inter", sans-serif;
  background: linear-gradient(rgba(20, 30, 40, 0.5), rgba(20, 30, 40, 0.6)),
    url("@/assets/img/Divisan/img_2.jpg");
  background-size: cover;
  background-position: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1.5rem;
}

.glass-login {
  width: 100%;
  max-width: 400px;
  background: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  padding: 3rem 2rem;
}

.brand-logo-container {
  width: 120px;
  height: 80px;
  background-color: #ffffff;
  /* Fondo blanco solicitado */
  border-radius: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  padding: 10px;
  /* Espacio interno para que el logo no toque los bordes */
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
}

.brand-logo-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.fw-black {
  font-weight: 900;
}

.tracking-widest {
  letter-spacing: 0.15em;
}

/* INPUTS MODERNOS */
.input-group-modern {
  position: relative;
  display: flex;
  align-items: center;
}

.input-group-modern i {
  position: absolute;
  left: 15px;
  color: rgba(255, 255, 255, 0.5);
  font-size: 1.1rem;
}

.input-group-modern input {
  width: 100%;
  background: rgba(0, 0, 0, 0.2) !important;
  border: 1px solid rgba(255, 255, 255, 0.1) !important;
  border-radius: 12px;
  padding: 12px 12px 12px 45px;
  color: white;
  transition: all 0.3s;
}

.input-group-modern input:focus {
  outline: none;
  background: rgba(0, 0, 0, 0.3) !important;
  border-color: rgba(255, 255, 255, 0.4) !important;
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.05);
}

.input-group-modern input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

/* CHECKBOX RECORDARME */
.checkbox-recordarme {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.75);
  font-size: 0.85rem;
  user-select: none;
}

.checkbox-recordarme input {
  width: 16px;
  height: 16px;
  accent-color: white;
  cursor: pointer;
}

/* BOTÓN PERSONALIZADO */
.btn-login {
  width: 100%;
  background: white;
  color: #1a1e23;
  border: none;
  padding: 14px;
  border-radius: 12px;
  font-weight: 700;
  letter-spacing: 0.05em;
  transition: all 0.3s;
}

.btn-login:hover:not(:disabled) {
  transform: translateY(-2px);
  background: #f0f0f0;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
}

.btn-login:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}
</style>
